import { debounce } from "../../helpers/utils";

export default {
  data() {
    return {
      scrollDisabled: false,
      allRecords: [],
      recordStart: 0,
      recordEnd: 0,
      currentRecords: [],
      batchSize: 5,
      maxRecords: 15,
      pixelLimit: 300
    };
  },
  computed: {
    possibleRecords(){
      // implement in component
      return [];
    }
  },
  methods: {
    disableScroll() {
      if (this.scrollDisabled) return;
      // logDetailed("disableScroll", "disableScroll");
      let el = this.getScrollingElement();
      this.scrollDisabled = true;
      el.addEventListener("DOMMouseScroll", disableEvents);
      el.addEventListener("wheel", disableEvents);
      el.addEventListener("mousewheel", disableEvents);
      el.addEventListener("DOMMouseScroll", disableEvents);
      el.addEventListener("touchmove", disableEvents);
    },
    enableScroll() {
      if (!this.scrollDisabled) return;
      // logDetailed("disableScroll", "enableScroll");
      let el = this.getScrollingElement();
      this.scrollDisabled = false;
      el.removeEventListener("DOMMouseScroll", disableEvents);
      el.removeEventListener("wheel", disableEvents);
      el.removeEventListener("mousewheel", disableEvents);
      el.removeEventListener("DOMMouseScroll", disableEvents);
      el.removeEventListener("touchmove", disableEvents);
    },
    getScrollingElement() {
      return this.$refs.wrapper;
    },
    setupRecords(){
      this.allRecords = [...this.possibleRecords];
      this.currentRecords = [];
      this.recordStart = 0;
      this.recordEnd = Math.min(this.maxRecords, this.allRecords.length);
      this.resetCurrentRecords();
    },
    handleScroll() {
      let el = this.$refs.wrapper;
      let oldScrollTop = el.scrollTop;
      let oldScrollHeight = el.scrollHeight;
      let dBottom = oldScrollHeight - oldScrollTop - el.clientHeight;

      let goingDown = dBottom <= this.pixelLimit;
      let goingUp = oldScrollTop <= this.pixelLimit;
      let canGetFromBottom = this.recordEnd < this.allRecords.length;
      let canGetFromTop = this.recordStart > 0;

      let oldStart = this.recordStart;
      let oldEnd = this.recordEnd;

      let rowWidth = this.countRowWidth();
      if (goingDown && canGetFromBottom)
        this.rollDown(rowWidth);
      else if (goingUp && canGetFromTop)
        this.rollUp(rowWidth);

      // if no changes, don't modify the records array
      let newStart = this.recordStart;
      let newEnd = this.recordEnd;
      if (newStart == oldStart && newEnd == oldEnd) 
        return Promise.resolve();
        
      this.resetCurrentRecords();

      // logDetailed("disableScroll", "handleScroll",
      //   "1. start/end changed",
      //   "oldStart", oldStart,
      //   "oldEnd", oldEnd,
      //   "newStart", newStart,
      //   "newEnd", newEnd,
      //   "oldScrollTop", oldScrollTop,
      //   "dBottom", dBottom);

      // if no change to start, don't mess with scroll position
      if (newStart == oldStart) return Promise.resolve();

      // disable scrolling while we fix the scroll position
      this.disableScroll();

      return this.fixScrollPosition(oldStart, oldEnd, newStart, goingDown, oldScrollTop);
    },
    rollDown(rowWidth){
      // logDetailed("disableScroll", "rollDown");
      let totalRecords = this.allRecords.length;
      let maxRecords = Math.min(this.maxRecords, totalRecords);
      let batch = this.batchSize - this.batchSize % rowWidth;
      this.recordStart = Math.min(totalRecords - maxRecords, this.recordStart + batch);
      this.recordEnd = this.recordStart + maxRecords;
    },
    rollUp(rowWidth){
      // logDetailed("disableScroll", "rollUp");
      let totalRecords = this.allRecords.length;
      let maxRecords = Math.min(this.maxRecords, totalRecords);
      let batch = this.batchSize - this.batchSize % rowWidth;
      this.recordStart = Math.max(0, this.recordStart - batch);
      this.recordEnd = this.recordStart + maxRecords;
    },
    reportUpdatedBounds() {
      // implement this in component
    },
    resetCurrentRecords() {
      this.currentRecords = this.allRecords.slice(this.recordStart, this.recordEnd);
      this.reportUpdatedBounds({ start: this.recordStart, end: this.recordEnd });
    },
    fixScrollPosition(oldStart, oldEnd, newStart, goingDown, oldScrollTop){
      // when start changes, the user is going to get kicked around
      // we want to counter scroll to improve the feel of the ui
      // we want to sort of anchor against the position of any element that's still in the view

      // an exam should maintain the same relative position to scrollTop
      // if scrolling down, grab the last exam
      // if scrolling up, grab the first exam
      //   want exam.offsetTop - view.scrollTop to be the same before and after load

      let indexBefore = 0;
      let indexAfter = oldStart - newStart;
      if (goingDown) {
        indexBefore = oldEnd - oldStart - 1;
        indexAfter = oldEnd - newStart - 1;
      }

      // find the position of the exam relative to the top before we add new exams
      let keepRow = this.getNthElement(indexBefore);
      let oldPosition = keepRow.offsetTop;
      // let oldRowText = keepRow.innerText;
      // wait for the new exams to be rendered
      return this.$nextTick().then(() => {
        // get the new position of the exam now that its position has been changed 
        //  by the addition/deletion of new exams to the top of the view
        let keepRow2 = this.getNthElement(indexAfter);
        let newScrollTop = this.$refs.wrapper.scrollTop;
        let newPosition = keepRow2.offsetTop;

        let oldDiff = oldScrollTop - oldPosition;
        let newDiff = newScrollTop - newPosition;

        let dDiff = oldDiff - newDiff;

        this.$refs.wrapper.scrollTop += dDiff;
        // logDetailed("disableScroll", "handleScroll",
        //   "2. new position of keep row",
        //   "oldRowPosition", oldPosition,
        //   "oldRowText", oldRowText,
        //   "newRowPosition", newPosition,
        //   "newRowText", keepRow2.innerText,
        //   "oldScrollTop", oldScrollTop,
        //   "newScrollTop", newScrollTop,
        //   "oldDiff", oldDiff,
        //   "newDiff", newDiff,
        //   "dDiff", dDiff);
        return Promise.resolve();
      });
    },
    countRowWidth(){
      let start = this.getNthElement(0).offsetLeft;
      let i = 1;
      while (i <= Math.min(this.maxRecords, this.allRecords.length) && this.getNthElement(i).offsetLeft != start)
        ++i;
      return i;
    },
    getNthElement(){
      // implement in component
    },
  },
  watch: {
    possibleRecords(){
      this.setupRecords();
    }
  },
  created() {
    this.debounceScroll = debounce(() => {
      if (this.scrollDisabled)
        return;
      // logDetailed("disableScroll", "debounceScroll");
      this.$nextTick()
        .then(() => this.handleScroll())
        .finally(() => this.$nextTick())
        .finally(() => setTimeout(() => this.enableScroll(), 150));
    }, 80);
  },
  mounted() {
    this.setupRecords();
  }
};

function disableEvents(e){
  e.preventDefault();
}